import React, { useContext } from "react";
import { net } from "../../../../database";
import Loading from "../../Loading";
import Search from "../../components/Search";
import useDBO from "../../hooks/useDBO";
import Lang from "../../components/Lang";
import sdate from "@timclark/sdate";
import "./salesMessages.css";
import ChatBox from "../../components/ChatBox";
import StudentLink from "../../components/StudentLink";
import Staff from "../../components/Staff";
import HtClass from "../../components/HtClass";
import { UserContext, StaffContext } from "../../../../Context";
import Rating from "../../components/Rating";

/**
 * SalesMessages - conversation list + chat pane for sales staff
 * @version 2.1.0
 * @author opencode glm-5.1
 * @date 2026-04-21
 */
export default function SalesMessages(props) {
  const [showClosed, setShowClosed] = React.useState("open");
  const messages = useDBO(`/manage/messages/${showClosed}/allstaff`);
  const [search, setSearch] = React.useState("");
  const [unanswered, setUnanswered] = React.useState(false);
  const [activeMessageId, setActiveMessageId] = React.useState(null);
  const { user } = useContext(UserContext);
  const { findStaff } = useContext(StaffContext);

  if (messages.loading) return <Loading />;

  let msgs = messages.get || [];

  if (unanswered) msgs = msgs.filter((m) => !m.chats || m.chats.length === 0);

  if (search !== "") {
    msgs = msgs.filter((m) => {
      if (
        m.studentsTagged
          ?.map((student) => student.name)
          .toString()
          .toLocaleUpperCase()
          .includes(search.toLocaleUpperCase())
      )
        return true;
      if (
        m.description?.toLocaleUpperCase().includes(search.toLocaleUpperCase())
      )
        return true;
      return false;
    });
  }

  msgs = msgs.map((m) => {
    let lastUpdate;
    if (!m.chats || m.chats.length < 1) {
      lastUpdate = m.createdAt;
    } else {
      const chat = m.chats[m.chats.length - 1];
      lastUpdate = chat.createdAt;
    }
    return { ...m, lastUpdate };
  });

  msgs.sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate));

  const activeMessage = msgs.find((m) => m._id === activeMessageId) || null;

  const closeMessage = (id) => {
    net.post(`/messages/${id}/close`).then((result) => {
      if (result.sent) {
        messages.remove(id);
        if (activeMessageId === id) setActiveMessageId(null);
      }
    });
  };

  const updateImportance = (id, value) => {
    net.post(`/messages/${id}/update`, { importance: value }).then((result) => {
      messages.set(messages.map((m) => (m._id === result._id ? result : m)));
    });
  };

  const fmtDateTime = (dateStr) => {
    const d = new Date(dateStr);
    const sd = new sdate(d);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${sd.datePad()}/${sd.monthPad()} ${hh}:${mm}`;
  };

  const fmtFullDateTime = (dateStr) => {
    const d = new Date(dateStr);
    const sd = new sdate(d);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${sd.fDate()} ${hh}:${mm}`;
  };

  const staffId = (val) => (typeof val === "object" && val?._id ? val._id : val);

  const senderName = (chat) => {
    if (typeof chat.user === "object" && chat.user?.name) return chat.user.name;
    if (typeof chat.user === "string") {
      const s = findStaff(chat.user);
      return s?.name || "Staff";
    }
    return "Staff";
  };

  return (
    <div className="sales-messages-page">
      <header className="flex justify-between items-center mb-4 px-2">
        <h1 className="sales-messages-title mb-0">
          <Lang w="messages" /> ({msgs.length})
        </h1>
      </header>

      <main className="sales-messages-wrapper">
        <aside className="conversation-sidebar">
          <div className="sidebar-header">
            <Search search={search} setSearch={setSearch} size="small" />
            <div className="sidebar-filters">
              <ShowToggleButtons
                value={showClosed}
                setValue={setShowClosed}
                buttons={[
                  { label: "Open", value: "open" },
                  { label: "Closed", value: "closed" },
                ]}
              />
              <button
                onClick={() => setUnanswered(!unanswered)}
                className={`h-8 px-3 text-xs font-extrabold rounded-[10px] transition-colors duration-150 ${
                  unanswered
                    ? "bg-teal-700 text-white"
                    : "border border-teal-300 text-teal-700 hover:bg-teal-50 bg-white"
                }`}
              >
                {unanswered ? "Show All" : "Unanswered"}
              </button>
            </div>
          </div>

          <div className="conversation-list">
            {msgs.length === 0 ? (
              <div className="p-10 text-center text-slate-400 italic font-medium">
                No conversations found
              </div>
            ) : (
              msgs.map((m) => (
                <div
                  key={m._id}
                  className={`conversation-item ${
                    activeMessageId === m._id ? "active" : ""
                  }`}
                  onClick={() => setActiveMessageId(m._id)}
                >
                  <div className="item-top">
                    <span className="item-name">
                      {m.studentsTagged?.[0]?.name
                        || (m.description?.length > 30
                          ? m.description.slice(0, 30) + "..."
                          : m.description || "Inquiry #" + m._id.slice(-4))}
                    </span>
                    <span className="item-time">
                      {fmtDateTime(m.lastUpdate)}
                    </span>
                  </div>
                  <div className="item-preview">
                    {m.chats?.length > 0
                      ? senderName(m.chats[m.chats.length - 1]) +
                        ": " +
                        m.chats[m.chats.length - 1].message
                      : m.description}
                  </div>
                  <div className="item-meta">
                    <span
                      className={`item-type-badge${
                        m.type === "Complaint" ? " is-complaint" : ""
                      }`}
                    >
                      {m.type}
                    </span>
                    <span className="item-staff">
                      <Staff id={staffId(m.recordedBy)} />
                    </span>
                    {m.who?.length > 0 && (
                      <span className="item-staff-assigned">
                        → <Staff id={staffId(m.who[0])} />
                      </span>
                    )}
                    {m.importance > 0 && (
                      <span className="importance-stars">
                        {"★".repeat(m.importance)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>

        <section className="chat-content-pane">
          {activeMessage ? (
            <React.Fragment>
              <header className="chat-pane-header">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <h2 className="sales-messages-heading">
                      {activeMessage.studentsTagged?.[0]?.name
                        || (activeMessage.description?.length > 40
                          ? activeMessage.description.slice(0, 40) + "..."
                          : activeMessage.description || "Conversation Details")}
                    </h2>
                    <span
                      className={`item-type-badge${
                        activeMessage.type === "Complaint" ? " is-complaint" : ""
                      }`}
                    >
                      {activeMessage.type}
                    </span>
                    <Rating
                      value={activeMessage.importance}
                      height={25}
                      setValue={(newValue) =>
                        updateImportance(activeMessage._id, newValue)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="timeline-date inline-block w-fit">
                      Opened {fmtFullDateTime(activeMessage.createdAt)} by{" "}
                      <Staff id={staffId(activeMessage.recordedBy)} />
                    </div>
                    {activeMessage.who?.length > 0 && (
                      <div className="allocated-badge">
                        <span className="font-bold">Allocated to:</span>
                        {activeMessage.who.map((w, i) => (
                          <React.Fragment key={staffId(w)}>
                            <Staff id={staffId(w)} />
                            {i < activeMessage.who.length - 1 ? ", " : ""}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {activeMessage.status === 0 && (
                    <button
                      disabled={!activeMessage.chats?.length}
                      onClick={() => closeMessage(activeMessage._id)}
                      className="close-inquiry-btn"
                    >
                      Close Inquiry
                    </button>
                  )}
                </div>
              </header>

              <div className="chat-pane-body">
                <div className="chat-description-section mb-6">
                  <span className="chat-description-label">
                    Initial Request
                  </span>
                  <div className="chat-description-box">
                    {activeMessage.description}
                  </div>
                </div>

                {activeMessage.studentsTagged?.length > 0 && (
                  <div className="mb-6">
                    <span className="chat-description-label">
                      Associated Records
                    </span>
                    <div className="student-tag-group">
                      {activeMessage.studentsTagged.map((s, idx) => (
                        <span key={s._id || idx} className="student-tag-sm">
                          <StudentLink student={s} showClasses={false} />
                        </span>
                      ))}
                      {activeMessage.classesTagged?.map((c, idx) => (
                        <span key={c._id || idx} className="student-tag-sm">
                          <HtClass htClass={c} />
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-auto chat-box-container shadow-inner rounded-2xl border border-slate-100">
                  <ChatBox
                    message={activeMessage}
                    user={user}
                    setMessages={messages.set}
                    messages={messages}
                  />
                </div>
              </div>
            </React.Fragment>
          ) : (
            <div className="empty-chat-state">
              <div className="empty-icon">💬</div>
              <p className="font-bold text-xl text-slate-400">
                Select a message from the sidebar to start chatting
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

/**
 * Segmented toggle control replacing MUI ToggleButtonGroup
 * @version 2.1.0
 * @author opencode glm-5.1
 * @date 2026-04-21
 */
function ShowToggleButtons(props) {
  const { value, setValue, buttons } = props;
  return (
    <div className="flex rounded-lg border border-slate-200 overflow-hidden h-8">
      {buttons.map((button) => (
        <button
          key={button.value}
          onClick={() => setValue(button.value)}
          className={`px-3 text-xs font-extrabold transition-colors duration-150 ${
            value === button.value
              ? "bg-teal-50 text-teal-800"
              : "bg-white text-slate-500 hover:bg-slate-50"
          }`}
        >
          {button.label}
        </button>
      ))}
    </div>
  );
}
